import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '@env/environment';
import { swedishTimestamp } from '@app/shared/swedish-time';

export interface MemberMessage {
  Id: string;
  Source: 'Personal' | 'General';
  Title: string;
  Summary: string;
  OccurredAtUtc: string;
  FirstReadAtUtc: string | null;
  IsRead: boolean;
  Version: string;
  RelatedType: 'Invoice' | 'Document' | 'Payout' | null;
  RelatedId: string | null;
  RelatedLabel: string | null;
  PushStatus?: string;
}
export interface MemberMessageDetail extends MemberMessage { Content: string; }
export interface MemberMessagePage { Items: MemberMessage[]; TotalCount: number; UnreadCount: number; }
export interface MemberMessageQuery {
  page: number;
  pageSize: number;
  unreadOnly: boolean;
  type: 'All' | 'Personal' | 'General';
  search: string;
}
export const defaultMessageQuery: MemberMessageQuery = { page: 0, pageSize: 25, unreadOnly: false, type: 'All', search: '' };

/** These contracts contain instants, unlike the older date-only message contract. */
export function messageTimestamp(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : swedishTimestamp(date).slice(0, 16).replace('T', ' ');
}

export function messagePushStatus(value?: string): string {
  const labels: Record<string, string> = {
    Pending: 'Väntar', Queued: 'Köad', Processing: 'Bearbetas', Sending: 'Skickas',
    Sent: 'Skickad till Apple', Accepted: 'Mottagen av Apple', Delivered: 'Levererad',
    Failed: 'Misslyckad', Retrying: 'Nytt försök väntar', Disabled: 'Avstängd',
    Skipped: 'Inte skickad', NoDevices: 'Ingen ansluten enhet', NotRequested: 'Ingen push begärd', NotApplicable: 'Inte aktuellt'
  };
  return value ? labels[value] || 'Status saknas' : 'Inte aktuellt';
}

@Injectable({ providedIn: 'root' })
export class MemberMessageService {
  constructor(private http: HttpClient) {}

  list(query: MemberMessageQuery, adminUserId: string | null = null): Observable<MemberMessagePage> {
    let params = this.params().set('page', query.page).set('pageSize', query.pageSize)
      .set('unreadOnly', query.unreadOnly).set('type', query.type).set('search', query.search);
    return this.http.get<MemberMessagePage>(this.url(adminUserId), { params });
  }

  detail(id: string, adminUserId: string | null = null): Observable<MemberMessageDetail> {
    return this.http.get<MemberMessageDetail>(`${this.url(adminUserId)}/${encodeURIComponent(id)}`, { params: this.params() });
  }

  read(id: string, version: string): Observable<{ FirstReadAtUtc: string | null }> {
    return this.http.post<{ FirstReadAtUtc: string | null }>(`${this.url()}/${encodeURIComponent(id)}/read`,
      { Version: version }, { params: this.params() });
  }

  preferences(): Observable<{ PushEnabled: boolean }> {
    return this.http.get<{ PushEnabled: boolean }>(`${this.url()}/preferences`);
  }

  savePreferences(pushEnabled: boolean): Observable<{ PushEnabled: boolean }> {
    return this.http.put<{ PushEnabled: boolean }>(`${this.url()}/preferences`, { PushEnabled: pushEnabled });
  }

  private params(): HttpParams { return new HttpParams().set('appId', env.appId); }
  private url(adminUserId: string | null = null): string {
    return env.hostApi + (adminUserId ? `admin/users/${encodeURIComponent(adminUserId)}/messages` : 'MemberMessages');
  }
}
